import { useEffect, useRef, useState } from "react"
import Api from "../api/api"
import { Policy, PolicyKind, POLICY_NAMES } from "../api/models"
import { useSignOut } from "../auth/signOut"
import Spinner from "./widgets/Spinner"

// Blocking consent to the legal documents (Terms of Use, Privacy
// Policy): shown instead of the app whenever /me reports a document with
// acceptanceRequired (never accepted, or a newer version that requires
// acceptance was published). Only those documents are listed, each with its
// own checkbox — consent is given per document, never bundled — and Accept
// enables once every one is ticked. Can't be dismissed: the only ways out
// are accepting or signing out. Acceptance is recorded server-side against
// the CURRENT versions; a 409 means a document changed while this was open,
// so the page reloads to show the new one.
export default function PolicyConsentDialog({ policies, accessToken, onAccepted }: {
  policies: Policy[]
  accessToken: string
  onAccepted: (policies: Policy[]) => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const signOut = useSignOut()
  const [checked, setChecked] = useState<Partial<Record<PolicyKind, boolean>>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const pending = policies.filter(p => p.acceptanceRequired)
  const allChecked = pending.every(p => checked[p.kind] === true)
  const anyUpdated = pending.some(p => p.acceptedVersion !== undefined)
  const names = pending.map(p => POLICY_NAMES[p.kind]).join(" and ")
  const plural = pending.length > 1 || pending.some(p => p.kind === "terms")

  const accept = async () => {
    setBusy(true)
    setError("")
    try {
      const versions = Object.fromEntries(pending.map(p => [p.kind, p.currentVersion])) as Partial<Record<PolicyKind, string>>
      const { policies: next } = await Api.acceptPolicies(accessToken, versions)
      onAccepted(next)
    } catch (err) {
      const message = err instanceof Error ? err.message : "unknown error"
      if (/current (terms|privacy) version/i.test(message)) {
        window.location.reload()
        return
      }
      setError(message)
      setBusy(false)
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onCancel={(e) => e.preventDefault()}
      onClose={() => dialogRef.current?.showModal()}
    >
      <div className="redeem-dialog-content">
        {/* "Terms" is grammatically plural on its own ("Our Terms of Use
            have changed"), so the verb follows the document, not only the count */}
        <h2 className="redeem-dialog-title">{anyUpdated ? `Our ${names} ${plural ? "have" : "has"} changed` : names}</h2>
        <p style={{ marginTop: 0 }}>
          {anyUpdated
            ? `Please review and accept the ${pending.length > 1 ? "documents" : "new version"} below to continue.`
            : `Please review and accept our ${names} to continue.`}
        </p>

        {pending.map(p => (
          <div key={p.kind} className="policy-consent-item">
            <p>
              <a href={p.url} target="_blank" rel="noopener noreferrer" className="card-link">
                Read the {POLICY_NAMES[p.kind]} (version {p.currentVersion})
              </a>
            </p>
            <label className="policy-consent">
              <input type="checkbox" checked={checked[p.kind] === true}
                onChange={(e) => setChecked({ ...checked, [p.kind]: e.target.checked })} disabled={busy} />
              <span>I have read and agree to the {POLICY_NAMES[p.kind]}.</span>
            </label>
          </div>
        ))}

        {error && <p className="generate-codes-error">{error}</p>}

        <div className="redeem-actions">
          <button onClick={() => signOut()} disabled={busy}>Sign out</button>
          <button className="btn-upgrade" disabled={busy || !allChecked} onClick={accept}>
            {busy ? <><Spinner size={12} /> Saving…</> : "Accept"}
          </button>
        </div>
      </div>
    </dialog>
  )
}
